import * as Sentry from "@sentry/react";
import { User } from "@/types/User";

const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;

// Only production builds report — local, integration, staging, and unit tests
// never emit events.
const reportingEnabled =
  Boolean(dsn) &&
  import.meta.env.MODE === "production" &&
  !import.meta.env.VITEST &&
  typeof window !== "undefined" &&
  window.location.hostname !== "localhost";

// API error messages can embed a user's email (PII); strip them everywhere in
// the event payload so they can't reach Sentry titles, alerts, or storage.
// event.user is left alone — id/email there is the sanctioned way to
// attribute an event to a user.
const EMAIL_PATTERN =
  /[A-Za-z0-9][^\s@/?:#&=<>()[\]\\,;"]*@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
const REDACTED = "REDACTED_EMAIL";

const redactValue = (value: unknown): unknown => {
  if (typeof value === "string") return value.replace(EMAIL_PATTERN, REDACTED);
  if (Array.isArray(value)) return value.map(redactValue);
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, entry]) => [key, redactValue(entry)])
    );
  }
  return value;
};

const redactEmailsInEvent = <T extends Sentry.Event>(event: T): T => {
  for (const exception of event.exception?.values ?? []) {
    if (exception.type)
      exception.type = exception.type.replace(EMAIL_PATTERN, REDACTED);
    if (exception.value)
      exception.value = exception.value.replace(EMAIL_PATTERN, REDACTED);
  }
  event.message = redactValue(event.message) as Sentry.Event["message"];
  event.contexts = redactValue(event.contexts) as Sentry.Event["contexts"];
  event.breadcrumbs = redactValue(
    event.breadcrumbs
  ) as Sentry.Event["breadcrumbs"];
  event.request = redactValue(event.request) as Sentry.Event["request"];
  event.extra = redactValue(event.extra) as Sentry.Event["extra"];
  event.tags = redactValue(event.tags) as Sentry.Event["tags"];
  return event;
};

// Client-side connectivity loss isn't actionable.
const isNetworkError = (event: Sentry.Event): boolean => {
  const exceptionValue = event.exception?.values?.[0]?.value;
  return typeof exceptionValue === "string"
    ? exceptionValue.toLowerCase().includes("network error")
    : false;
};

if (reportingEnabled) {
  Sentry.init({
    dsn,
    // Tracks the build mode ("production"/"staging"/"integration") instead of
    // a constant so non-prod builds can't report as production.
    environment: import.meta.env.MODE.toLowerCase(),
    // Shared with the vite.config.ts upload plugin so sourcemaps always match.
    release: __SENTRY_RELEASE__,
    // Sampling rates follow the org convention (bilt-frontend defaultSentryConfig).
    sampleRate: 0.75,
    tracesSampleRate: 0.025,
    integrations: [Sentry.browserTracingIntegration()],
    sendDefaultPii: false,
    // Same browser-extension noise the Raygun onBeforeSend in UserProvider
    // discards (Firefox iOS and Brave/MetaMask injections).
    ignoreErrors: [
      /window\.__firefox__/i,
      /window\.ethereum/i,
      /__firefox__\.metadata/i,
      /__firefox__\.includeOnce/i,
      /can't find variable: __firefox__/i,
      /Cannot read properties of undefined \(reading 'ethereum'\)/i,
      // Unhandled rejections from third-party fetches a client can't reach:
      // Stripe.js injection and LaunchDarkly flag polling (handled:no).
      /Failed to load Stripe\.js/,
      /LaunchDarklyFlagFetchError/
    ],
    beforeSend(event) {
      if (isNetworkError(event)) return null;
      return redactEmailsInEvent(event);
    }
  });
}

export const setSentryUser = ({ id, email }: Pick<User, "id" | "email">) => {
  if (!reportingEnabled) return;
  Sentry.setUser({ id: String(id), email });
};

// Logout navigates in-app without a reload, so the previous resident would
// otherwise stay attached to events on the logged-out pages.
export const clearSentryUser = () => {
  if (!reportingEnabled) return;
  Sentry.setUser(null);
};

export const captureError = (
  error: unknown,
  data?: unknown,
  tags?: Record<string, string>
) => {
  if (!reportingEnabled) return;
  // String payloads get wrapped so a native stack trace is captured.
  const exception = typeof error === "string" ? new Error(error) : error;
  Sentry.captureException(exception, { extra: { data }, tags });
};
